/* eslint-disable */
import cn from 'classnames';
import get from 'lodash/get';
import {
  DELIVERY_ORDER_STATUS_ALL,
  DELIVERY_ORDER_STATUS_COMPLETED,
  DELIVERY_ORDER_STATUS_DELIVERED,
  DELIVERY_ORDER_STATUS_LIVE,
  DELIVERY_ORDER_STATUS_SCHEDULED,
  DELIVERY_SERVICE_BASE_URL,
} from '../../Constants/Delivery/Delivery.constants';
import throwDeliveryInvalidOrderStatusError from '../../Errors/Delivery/DeliveryInvalidOrderStatus.error';
import throwModelNotFoundByIdError from '../../Errors/Model/ModelNotFoundById.error';
import { deliveriesFixture } from './Delivery.service.fixture';
import './Delivery.service.i18n';

/**
 * Handle delivery tasks
 * @class DeliveryService
 */
class DeliveryService {
  /**
   * The base url to request deliveries from
   * @type {string}
   * @memberof DeliveryService
   */
  _baseUrl = DELIVERY_SERVICE_BASE_URL;

  /**
   * The `AuthService`
   * @type {AuthService}
   * @memberof DeliveryService
   */
  authService;

  /**
   * The query that we need to create a `DeliveryEntity`
   * @type {string}
   * @memberof DeliveryService
   */
  deliveriesGraphQL = `{
    customer {
      name
    }
    docketId
    docketTime
    instructions {
      comments
      followTruckId
    }
    order {
      status
    }
    plant {
      geo {
        lat
        lon
      }
    }
    product {
      additives
      aggregatedSize
      name
      slump
    }
    quantity {
      total
    }
    site {
      address
    }
    truckNumber
  }`;

  /**
   * The `HttpService`
   * @type {HttpService}
   * @memberof DeliveryService
   */
  httpService;

  /**
   * Creates an instance of `DeliveryService`
   * @param {DeliveryServiceCreateDto} dto The params required to create an instance of `DeliveryService`
   * @memberof DeliveryService
   */
  constructor({ authService, httpService }) {
    this.authService = authService;
    this.httpService = httpService;
  }

  /**
   * Fetch all known deliveries
   * @returns {Promise<Array<DeliveryEntity>>} The deliveries
   * @memberof DeliveryService
   */
  all = async () => {
    const headers = this.authService.createHeaders();
    headers.append(`content-type`, `text/plain`);
    /** @type {Array<DeliveryEntity> | null} */
    const deliveries = await this.httpService.post(this._baseUrl, {
      data: `{
        findDockets(
          plantCode:"TSZ"
        )
        ${this.deliveriesGraphQL}
      }`,
      headers,
    });
    if (!Array.isArray(deliveries)) {
      return [];
    }
    return deliveries;
  };

  /**
   * Fetch all known deliveries for specific date
   * @param {DeliveryServiceFindByDateDto} date The string specifying date in format YYYY-MM-DD to retrieve deliveries for
   * @returns {Promise<Array<DeliveryEntity>>} The deliveries
   * @memberof DeliveryService
   */
  findByDate = async ({ date }) => {
    const headers = this.authService.createHeaders();
    headers.append(`content-type`, `text/plain`);
    /** @type {Array<DeliveryEntity> | null} */
    const deliveries = await this.httpService.post(this._baseUrl, {
      data: `{
        findDockets(
          date: "${date}"
          plantCode: "TSZ",
        )
        ${this.deliveriesGraphQL}
      }`,
      headers,
    });
    if (!Array.isArray(deliveries)) {
      return [];
    }
    return deliveries;
  };

  /**
   * Find a delivery by its `id`
   * @param {DeliveryServiceFindByIdDto} dto The required params to find an delivery by its `id`
   * @returns {Promise<DeliveryEntity>} The found delivery
   * @memberof DeliveryService
   */
  findById = async ({ id }) => {
    const foundDelivery = deliveriesFixture.find((delivery) => delivery.docketId === id);
    if (foundDelivery === undefined) {
      throwModelNotFoundByIdError(`delivery`, id);
    }
    return foundDelivery;
  };

  /**
   * Determine which JSS classes to use to style the status for the given `delivery`
   * @param {DeliveryEntity} delivery The `delivery` to get the classes for
   * @param {Object} classes TThe `classes` specific to this component
   * @returns {string} The _stringified_ representation fo the classnames
   * @memberof DeliveriesPageComponent
   */
  getPillClasses = (delivery, classes) => {
    const status = get(delivery, `order.status`);
    const pillClasses = [classes.deliveryServiceStatusPill];
    switch (status) {
      case DELIVERY_ORDER_STATUS_COMPLETED: {
        pillClasses.push(classes.deliveryServiceStatusPillComplete);
        break;
      }
      case DELIVERY_ORDER_STATUS_DELIVERED: {
        pillClasses.push(classes.deliveryServiceStatusPillDelivered);
        break;
      }
      case DELIVERY_ORDER_STATUS_LIVE: {
        pillClasses.push(classes.deliveryServiceStatusPillLive);
        break;
      }
      case DELIVERY_ORDER_STATUS_SCHEDULED: {
        pillClasses.push(classes.deliveryServiceStatusPillScheduled);
        break;
      }
      case DELIVERY_ORDER_STATUS_ALL:
      // Don't style ALL
      // fall through
      default: {
        throwDeliveryInvalidOrderStatusError(status);
      }
    }
    return cn(pillClasses);
  };

  /**
   * Update a delivery with a specific `id`
   * @param {DeliveryServiceUpdateByIdDto} dto The required params to update a delivery with a specific `id`
   * @returns {Promise<DeliveryEntity>} The found delivery
   * @memberof DeliveryService
   */
  updateById = async ({ changes, id }) => {
    const index = deliveriesFixture.findIndex((delivery) => delivery.docketId === id);
    if (index === -1) {
      throwModelNotFoundByIdError(`delivery`, id);
    }
    deliveriesFixture[index] = {
      ...deliveriesFixture[index],
      ...changes,
    };
    return deliveriesFixture[index];
  };
}

export default DeliveryService;
